import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleFilled, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import parse, { domToReact } from 'html-react-parser';
import content from './benefits.json';
import './style.scss';

// Preserve the source copy and formatting without allowing navigation from this page.
const textOnlyOptions = {
  replace: node => {
    if (node.name === 'a') {
      return <span>{domToReact(node.children, textOnlyOptions)}</span>;
    }
  }
};

function renderBenefitContent(html) {
  return parse(html, textOnlyOptions);
}

function GuaranteeDialog({ initialId, onClose }) {
  const [selectedId, setSelectedId] = useState(initialId);
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const tabsRef = useRef(null);
  const closeRef = useRef(null);
  const detail = content.guarantees.find(item => item.id === selectedId);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const container = document.getElementById('container');
    const scrollElements = [document.body, container].filter(Boolean);
    const overflow = scrollElements.map(element => element.style.overflow);
    scrollElements.forEach(element => { element.style.overflow = 'hidden'; });
    closeRef.current.focus();

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current.querySelectorAll('button:not([disabled]):not([tabindex="-1"])');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      scrollElements.forEach((element, index) => { element.style.overflow = overflow[index]; });
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [onClose]);

  useEffect(() => {
    const tabs = tabsRef.current;
    const selected = tabs.querySelector('[aria-selected="true"]');
    // Only scroll the tab strip; scrollIntoView would also move the detail page.
    const left = selected.offsetLeft;
    const right = left + selected.offsetWidth;
    if (left < tabs.scrollLeft) tabs.scrollLeft = left;
    else if (right > tabs.scrollLeft + tabs.clientWidth) tabs.scrollLeft = right - tabs.clientWidth;
  }, [selectedId]);

  function selectDetail(id) {
    setSelectedId(id);
    bodyRef.current.scrollTop = 0;
  }

  function handleTabKeyDown(event) {
    const current = content.guarantees.findIndex(item => item.id === selectedId);
    const last = content.guarantees.length - 1;
    const next = {
      ArrowRight: (current + 1) % content.guarantees.length,
      ArrowLeft: (current + last) % content.guarantees.length,
      Home: 0,
      End: last
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    selectDetail(content.guarantees[next].id);
    tabsRef.current.querySelectorAll('[role="tab"]')[next].focus();
  }

  return createPortal(
    <div className="marriott-benefits-dialog-mask" onClick={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="marriott-benefits-dialog" role="dialog" aria-modal="true"
        aria-labelledby="marriott-detail-title" ref={dialogRef}>
        <header className="marriott-benefits-dialog-header">
          <h2 id="marriott-detail-title">保证详情</h2>
          <button type="button" ref={closeRef} onClick={onClose} aria-label="关闭权益详情" title="关闭权益详情">
            <CloseOutlined aria-hidden="true" />
          </button>
        </header>
        <div className="marriott-benefits-dialog-body" ref={bodyRef} role="region" aria-label="权益详情正文">
          <div className="marriott-benefits-detail-nav" ref={tabsRef} role="tablist"
            aria-label="保证详情分类" onKeyDown={handleTabKeyDown}>
            {content.guarantees.map(item => (
              <button type="button" key={item.id} role="tab" id={`marriott-detail-tab-${item.id}`}
                aria-selected={item.id === selectedId} aria-controls="marriott-detail-panel"
                tabIndex={item.id === selectedId ? 0 : -1}
                onClick={() => selectDetail(item.id)}>{item.title}</button>
            ))}
          </div>
          <section id="marriott-detail-panel" role="tabpanel" aria-labelledby={`marriott-detail-tab-${detail.id}`}>
            <ul className="marriott-benefits-detail-tiers">
              {detail.tiers.map(name => {
                const tier = content.tiers.find(item => item.name === name);
                return <li key={name}><CheckCircleFilled aria-hidden="true" className={`tier-${tier.id}`} />{name}</li>;
              })}
            </ul>
            <div className="marriott-benefits-detail-copy">{renderBenefitContent(detail.introduction)}</div>
            {detail.rows.length > 0 && (
              <table className="marriott-benefits-detail-table" aria-label={`${detail.title}品牌及补偿`}>
                <thead><tr>{detail.columns.map(column => <th scope="col" key={column}>{column}</th>)}</tr></thead>
                <tbody>{detail.rows.map((row, index) => (
                  <tr key={index}>{row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cellIndex === 2 ? <span className="marriott-benefits-compensation">{renderBenefitContent(cell)}</span> : renderBenefitContent(cell)}</td>
                  ))}</tr>
                ))}</tbody>
              </table>
            )}
            <div className={`marriott-benefits-detail-copy ${detail.rows.length ? 'marriott-benefits-detail-notes' : 'marriott-benefits-detail-terms'}`}>{renderBenefitContent(detail.notes)}</div>
          </section>
        </div>
      </section>
    </div>, document.body
  );
}

export default function MarriottBenefits() {
  const [detailId, setDetailId] = useState(null);
  const closeDetail = useCallback(() => setDetailId(null), []);

  return (
    <main className="marriott-benefits" lang="zh-CN" aria-label="万豪旅享家会员礼遇" aria-hidden={!!detailId}>
      <div className="marriott-benefits-content" id="elitebenefitsbytier">
 
        <h1 className="hotel-member-title">
          <img className="hotel-member-logo" src="https://staticfile.badazhou.com/20260921/f42723783af2718454a9e9ef8c303b64.png" alt="" />
          <span>万豪旅享家-会员礼遇</span>
        </h1>
        
        <table className="marriott-benefits-table">
          <caption className="marriott-benefits-sr-only">按级别提供的尊贵级别礼遇</caption>
          <thead>
            <tr aria-hidden="true"><td colSpan={5} className="marriott-benefits-table-title">按级别提供的尊贵级别礼遇</td></tr>
            <tr>{content.tiers.map(tier => (
              <th id={`marriott-tier-${tier.id}`} key={tier.id} scope="col" className={`tier-${tier.id}`}>
                {tier.name}
              </th>
            ))}</tr>
            <tr className="marriott-benefits-requirements">{content.tiers.map(tier => (
              <td key={tier.id}>{tier.requirement}</td>
            ))}</tr>
          </thead>
          {content.benefits.map(benefit => (
            <tbody key={benefit.id}>
              <tr>
                <th colSpan={5} scope="rowgroup" id={`marriott-benefit-${benefit.id}`} className="marriott-benefits-description">
                  <h2>
                    {benefit.detailId ? (
                      <button type="button" className="marriott-benefits-link" aria-haspopup="dialog"
                        onClick={() => setDetailId(benefit.detailId)}>{benefit.title}</button>
                    ) : benefit.title}
                  </h2>
                  <p>{benefit.description}</p>
                </th>
              </tr>
              <tr className="marriott-benefits-values">{benefit.cells.map((value, index) => (
                <td key={content.tiers[index].id} className={typeof value === 'string' && value ? 'marriott-benefits-text-value' : undefined}
                  headers={`marriott-benefit-${benefit.id} marriott-tier-${content.tiers[index].id}`}>
                  {value === true ? (
                    <span className={`marriott-benefits-check tier-${content.tiers[index].id}`} role="img" aria-label="享有此礼遇">
                      <CheckOutlined aria-hidden="true" />
                    </span>
                  ) : value || <span className="marriott-benefits-sr-only">不适用</span>}
                </td>
              ))}</tr>
            </tbody>
          ))}
        </table>
        <div className="marriott-benefits-disclaimer">{renderBenefitContent(content.disclaimer)}</div>
      </div>
      {detailId && <GuaranteeDialog initialId={detailId} onClose={closeDetail} />}
    </main>
  );
}
